import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Sparkles, RefreshCw, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.cohadmin{background:#0D1B2A;color:#FAF7F0;min-height:100vh;font-family:Inter,system-ui,sans-serif;}
.cohadmin-shell{max-width:1500px;margin:0 auto;padding:48px 32px 96px;}
.cohadmin-head{display:flex;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;gap:16px;margin-bottom:32px;}
.cohadmin-eyebrow{font-size:11px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:8px;}
.cohadmin-h1{font-family:'Cormorant Display',serif;font-size:44px;font-weight:700;line-height:1.05;letter-spacing:-.02em;margin:0;}
.cohadmin-h1 em{font-style:italic;color:#C8A84B;}
.cohadmin-sub{color:rgba(250,247,240,.6);font-size:13px;margin-top:8px;}

.cohadmin-actions{display:flex;gap:10px;flex-wrap:wrap;}
.cohbtn{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;padding:11px 22px;border-radius:50px;border:1px solid;cursor:pointer;display:inline-flex;align-items:center;gap:8px;transition:all .2s;}
.cohbtn.primary{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.cohbtn.primary:hover{background:#FAF7F0;}
.cohbtn.line{background:transparent;color:#FAF7F0;border-color:rgba(200,168,75,.32);}
.cohbtn.line:hover{border-color:#C8A84B;color:#C8A84B;}
.cohbtn:disabled{opacity:.5;cursor:not-allowed;}

.cohstats{display:grid;grid-template-columns:repeat(5,1fr);gap:1px;background:rgba(200,168,75,.18);border:1px solid rgba(200,168,75,.18);margin-bottom:32px;}
.cohstat{background:#15243A;padding:22px 24px;}
.cohstat-l{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
.cohstat-n{font-family:'Cormorant Display',serif;font-size:36px;font-weight:700;color:#FAF7F0;line-height:1;margin-top:6px;}

.cohtable{width:100%;border-collapse:collapse;font-size:13px;background:#15243A;border:1px solid rgba(200,168,75,.18);}
.cohtable th{text-align:left;padding:14px 16px;font-size:9px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#C8A84B;background:#0D1B2A;border-bottom:1px solid rgba(200,168,75,.18);}
.cohtable td{padding:18px 16px;border-bottom:1px solid rgba(200,168,75,.06);vertical-align:top;}
.cohtable tr.row-clickable{cursor:pointer;}
.cohtable tr.row-clickable:hover{background:rgba(200,168,75,.04);}
.cohtable .name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#FAF7F0;line-height:1.15;}
.cohtable .meta{font-size:11px;color:rgba(250,247,240,.55);margin-top:4px;}
.cohtable .one-liner{font-size:13px;color:rgba(250,247,240,.85);max-width:340px;line-height:1.5;}

.cohscore{display:flex;align-items:center;gap:8px;}
.cohscore-num{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;line-height:1;}
.cohscore-num.high{color:#10b981;}
.cohscore-num.mid{color:#C8A84B;}
.cohscore-num.low{color:#ef4444;}
.cohscore-num.none{color:rgba(250,247,240,.3);font-style:italic;font-size:18px;}
.cohscore-bar{width:80px;height:4px;background:rgba(255,255,255,.08);position:relative;border-radius:2px;overflow:hidden;}
.cohscore-bar > i{position:absolute;left:0;top:0;bottom:0;background:#C8A84B;}

.cohbadge{display:inline-block;font-size:9px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:4px 10px;border-radius:999px;border:1px solid;}
.cohbadge.fast_track{background:rgba(16,185,129,.12);color:#10b981;border-color:rgba(16,185,129,.4);}
.cohbadge.interview{background:rgba(200,168,75,.12);color:#C8A84B;border-color:rgba(200,168,75,.4);}
.cohbadge.watchlist{background:rgba(255,255,255,.05);color:rgba(250,247,240,.7);border-color:rgba(250,247,240,.2);}
.cohbadge.decline{background:rgba(239,68,68,.1);color:#ef4444;border-color:rgba(239,68,68,.4);}
.cohbadge.submitted{background:rgba(255,255,255,.05);color:rgba(250,247,240,.55);border-color:rgba(250,247,240,.2);}
.cohbadge.scored{background:rgba(99,102,241,.12);color:#a5b4fc;border-color:rgba(99,102,241,.4);}
.cohbadge.shortlisted{background:rgba(200,168,75,.16);color:#C8A84B;border-color:rgba(200,168,75,.5);}
.cohbadge.accepted{background:rgba(16,185,129,.16);color:#10b981;border-color:rgba(16,185,129,.5);}
.cohbadge.rejected{background:rgba(239,68,68,.12);color:#ef4444;border-color:rgba(239,68,68,.4);}

.cohrow-detail{background:#0D1B2A;}
.cohrow-detail .grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;padding:28px 32px;}
.cohrow-detail h4{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;margin:0 0 10px;}
.cohrow-detail p, .cohrow-detail li{font-size:13px;color:rgba(250,247,240,.8);line-height:1.7;}
.cohrow-detail ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px;}
.cohrow-detail li{padding-left:18px;position:relative;}
.cohrow-detail li::before{content:'';position:absolute;left:0;top:9px;width:5px;height:5px;background:#C8A84B;transform:rotate(45deg);}
.cohaxes{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:18px;}
.cohaxis{background:#15243A;padding:12px 14px;border-left:2px solid #C8A84B;}
.cohaxis-l{font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:rgba(200,168,75,.7);font-weight:700;}
.cohaxis-n{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#FAF7F0;line-height:1;margin-top:4px;}

.cohstatusbar{display:flex;gap:8px;flex-wrap:wrap;margin-top:14px;}
.cohstatusbar button{font-size:9px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:7px 14px;border-radius:50px;border:1px solid rgba(200,168,75,.32);background:transparent;color:#FAF7F0;cursor:pointer;transition:all .2s;}
.cohstatusbar button:hover{border-color:#C8A84B;color:#C8A84B;}
.cohstatusbar button.active{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
`;

function scoreBucket(n) {
  if (n == null) return 'none';
  if (n >= 75) return 'high';
  if (n >= 55) return 'mid';
  return 'low';
}

function ApplicationRow({ app, onScore, onUpdateStatus, scoring }) {
  const [open, setOpen] = useState(false);
  const ai = app.ai_score;
  const overall = ai?.overall;
  const recommendation = ai?.recommendation || 'submitted';
  return (
    <>
      <tr className="row-clickable" onClick={() => setOpen((o) => !o)} data-testid={`coh-row-${app.id}`}>
        <td>
          <div className="name">{app.company_name}</div>
          <div className="meta">{app.founder_name} · {app.founder_email}</div>
        </td>
        <td><div className="one-liner">{app.one_liner}</div></td>
        <td>
          <div className="meta" style={{ marginTop: 0 }}>{app.industry}</div>
          <div className="meta">{app.funding_stage}</div>
        </td>
        <td>
          <div className="cohscore">
            <span className={`cohscore-num ${scoreBucket(overall)}`}>{overall != null ? Math.round(overall) : '—'}</span>
            {overall != null && <div className="cohscore-bar"><i style={{ width: `${Math.min(100, overall)}%` }} /></div>}
          </div>
        </td>
        <td>{ai && <span className={`cohbadge ${recommendation}`}>{recommendation.replace('_', ' ')}</span>}</td>
        <td><span className={`cohbadge ${app.status || 'submitted'}`}>{app.status}</span></td>
        <td>
          <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <button
              className="cohbtn line"
              style={{ padding: '7px 14px', fontSize: 10 }}
              disabled={scoring === app.id}
              onClick={() => onScore(app.id)}
              data-testid={`coh-score-${app.id}`}
            >
              {scoring === app.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {ai ? 'Re-score' : 'Score'}
            </button>
            <button className="cohbtn line" style={{ padding: '7px 12px', fontSize: 10 }} onClick={() => setOpen((o) => !o)}>
              {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="cohrow-detail" data-testid={`coh-detail-${app.id}`}>
          <td colSpan={7} style={{ padding: 0 }}>
            {ai ? (
              <div style={{ padding: '24px 32px' }}>
                <div className="cohaxes">
                  <div className="cohaxis"><div className="cohaxis-l">Governance</div><div className="cohaxis-n">{Math.round(ai.governance_readiness)}</div></div>
                  <div className="cohaxis"><div className="cohaxis-l">Traction</div><div className="cohaxis-n">{Math.round(ai.traction)}</div></div>
                  <div className="cohaxis"><div className="cohaxis-l">Founder fit</div><div className="cohaxis-n">{Math.round(ai.founder_fit)}</div></div>
                  <div className="cohaxis"><div className="cohaxis-l">UAE relevance</div><div className="cohaxis-n">{Math.round(ai.uae_relevance)}</div></div>
                </div>
                <div className="grid" style={{ padding: 0 }}>
                  <div>
                    <h4>Verdict</h4>
                    <p>{ai.verdict}</p>
                    <h4 style={{ marginTop: 18 }}>Strengths</h4>
                    <ul>{(ai.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                  <div>
                    <h4>Risks</h4>
                    <ul>{(ai.risks || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                    <h4 style={{ marginTop: 18 }}>Interview questions</h4>
                    <ul>{(ai.next_questions || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                </div>
                <div className="cohstatusbar">
                  {['shortlisted', 'accepted', 'rejected'].map((s) => (
                    <button
                      key={s}
                      className={app.status === s ? 'active' : ''}
                      onClick={() => onUpdateStatus(app.id, s)}
                      data-testid={`coh-status-${s}-${app.id}`}
                    >Mark {s}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '24px 32px' }}>
                <div className="grid" style={{ padding: 0 }}>
                  <div>
                    <h4>One-liner</h4>
                    <p>{app.one_liner}</p>
                    <h4 style={{ marginTop: 18 }}>Problem</h4>
                    <p>{app.problem}</p>
                    <h4 style={{ marginTop: 18 }}>Solution</h4>
                    <p>{app.solution}</p>
                  </div>
                  <div>
                    <h4>Why UAE</h4>
                    <p>{app.why_uae}</p>
                    <h4 style={{ marginTop: 18 }}>Traction</h4>
                    <p>{app.traction || 'None disclosed'}</p>
                    {(app.linkedin_url || app.website) && (
                      <>
                        <h4 style={{ marginTop: 18 }}>Links</h4>
                        <p>
                          {app.linkedin_url && <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#C8A84B', marginRight: 12 }}>LinkedIn <ExternalLink className="inline h-3 w-3" /></a>}
                          {app.website && <a href={app.website} target="_blank" rel="noopener noreferrer" style={{ color: '#C8A84B' }}>Website <ExternalLink className="inline h-3 w-3" /></a>}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminCohortApplicants() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(null);
  const [bulkScoring, setBulkScoring] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('overall');

  useEffect(() => { document.title = 'Cohort 1 Applicants · SKI AXIS Admin'; }, []);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy };
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/cohort/applications`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(data.applications || []);
    } catch (e) {
      console.error('fetchApps failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchApps(); /* eslint-disable-next-line */ }, [token, statusFilter, sortBy]);

  const handleScore = async (id) => {
    setScoring(id);
    try {
      const { data } = await axios.post(`${API}/admin/cohort/applications/${id}/score`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, ai_score: data.ai_score, status: 'scored' } : a)));
    } catch (e) {
      alert('AI scoring failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setScoring(null);
    }
  };

  const handleBulkScore = async () => {
    if (!window.confirm('Score every unscored Cohort 1 application with GPT-5.2? This uses your Universal Key balance.')) return;
    setBulkScoring(true);
    try {
      const { data } = await axios.post(`${API}/admin/cohort/applications/score-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 600000,
      });
      alert(`Scored ${data.scored} applications · ${data.failed} failed.`);
      fetchApps();
    } catch (e) {
      alert('Bulk scoring failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBulkScoring(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/cohort/applications/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (e) {
      alert('Status update failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const stats = useMemo(() => {
    const total = apps.length;
    const scored = apps.filter((a) => !!a.ai_score).length;
    const fast = apps.filter((a) => a.ai_score?.recommendation === 'fast_track').length;
    const interviews = apps.filter((a) => a.ai_score?.recommendation === 'interview').length;
    const accepted = apps.filter((a) => a.status === 'accepted').length;
    return { total, scored, fast, interviews, accepted };
  }, [apps]);

  if (!user) return null;

  return (
    <div className="cohadmin">
      <style>{STYLES}</style>
      <div className="cohadmin-shell">
        <div className="cohadmin-head">
          <div>
            <div className="cohadmin-eyebrow">SKI AXIS · Admin</div>
            <h1 className="cohadmin-h1">Cohort 1 <em>Applicants</em></h1>
            <p className="cohadmin-sub">AI-scored against governance, traction, founder fit, and UAE-relevance · Cohort opens July 1, 2026</p>
          </div>
          <div className="cohadmin-actions">
            <button className="cohbtn line" onClick={fetchApps} disabled={loading} data-testid="coh-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
            <button className="cohbtn primary" onClick={handleBulkScore} disabled={bulkScoring} data-testid="coh-score-all">
              {bulkScoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Score all unscored
            </button>
            <button className="cohbtn line" onClick={() => navigate('/admin')} data-testid="coh-back">← Admin</button>
          </div>
        </div>

        <div className="cohstats">
          <div className="cohstat"><div className="cohstat-l">Total Applications</div><div className="cohstat-n">{stats.total}</div></div>
          <div className="cohstat"><div className="cohstat-l">Scored by AI</div><div className="cohstat-n">{stats.scored}</div></div>
          <div className="cohstat"><div className="cohstat-l">Fast-Track</div><div className="cohstat-n">{stats.fast}</div></div>
          <div className="cohstat"><div className="cohstat-l">Interview</div><div className="cohstat-n">{stats.interviews}</div></div>
          <div className="cohstat"><div className="cohstat-l">Accepted</div><div className="cohstat-n">{stats.accepted}</div></div>
        </div>

        <div className="cohstatusbar" style={{ marginBottom: 16 }}>
          {['', 'submitted', 'scored', 'shortlisted', 'accepted', 'rejected'].map((s) => (
            <button
              key={s || 'all'}
              className={statusFilter === s ? 'active' : ''}
              onClick={() => setStatusFilter(s)}
              data-testid={`coh-filter-${s || 'all'}`}
            >{s || 'All'}</button>
          ))}
          <span style={{ flex: 1 }} />
          <button className={sortBy === 'overall' ? 'active' : ''} onClick={() => setSortBy('overall')} data-testid="coh-sort-overall">Sort: Score</button>
          <button className={sortBy === 'created_at' ? 'active' : ''} onClick={() => setSortBy('created_at')} data-testid="coh-sort-date">Sort: Newest</button>
        </div>

        <table className="cohtable">
          <thead>
            <tr>
              <th style={{ width: 240 }}>Company / Founder</th>
              <th>One-liner</th>
              <th style={{ width: 130 }}>Industry</th>
              <th style={{ width: 130 }}>Score</th>
              <th style={{ width: 110 }}>AI Verdict</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && apps.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 60, textAlign: 'center', color: 'rgba(250,247,240,.5)' }}><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading applications…</td></tr>
            )}
            {!loading && apps.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 60, textAlign: 'center', color: 'rgba(250,247,240,.5)' }}>
                No applications yet. Share <code style={{ color: '#C8A84B' }}>/cohort-1/apply</code> to start collecting.
              </td></tr>
            )}
            {apps.map((app) => (
              <ApplicationRow
                key={app.id}
                app={app}
                onScore={handleScore}
                onUpdateStatus={handleUpdateStatus}
                scoring={scoring}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
